import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Avatar } from '../../shared/ui/Avatar';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

type Item = {
  id: string; kind: 'testimonio' | 'gratitud'; title: string | null; body: string; status: 'pendiente' | 'publicado' | 'oculto'; createdAt: string;
  amens: number; amenByMe: boolean; mine: boolean; anonymous: boolean; author: { personId: string; name: string } | null;
};
const day = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short' });

/** Muro de gratitud y testimonios (con revisión de Pastoral). */
export function TestimoniesPage() {
  const me = useMe();
  const base = `/prayer/churches/${me.activeChurchId}/testimonies`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<{ canReview: boolean; items: Item[] }>(base, signal) : Promise.resolve(null)), [base]);
  const [params, setParams] = useSearchParams();
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const d = data.data;
  if (!d) return null;
  const tabs: [string, string][] = [['gratitud', 'Muro de gratitud'], ['testimonios', 'Testimonios'], ...(d.canReview ? [['revisar', `Por revisar (${d.items.filter((i) => i.status === 'pendiente').length})`] as [string, string]] : [])];
  const tab = tabs.find(([k]) => k === params.get('ver'))?.[0] ?? 'gratitud';
  const run = async (action: () => Promise<unknown>, ok: string | null) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      if (ok) setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const shown = d.items.filter((i) => (tab === 'revisar' ? i.status === 'pendiente' : tab === 'gratitud' ? i.kind === 'gratitud' && i.status === 'publicado' : i.kind === 'testimonio' && (i.status === 'publicado' || i.mine)));

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Gratitud y testimonios</h1>
        <p className="muted">Lo que Dios está haciendo en nuestra iglesia.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <div className="tabs" role="tablist" aria-label="Gratitud y testimonios">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ ver: k }, { replace: true })}>{l}</button>
        ))}
      </div>
      {tab !== 'revisar' ? (
        <Card title={tab === 'gratitud' ? 'Doy gracias por…' : 'Comparte tu testimonio'}>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const f = new FormData(form);
              const kind = tab === 'gratitud' ? 'gratitud' : 'testimonio';
              void run(
                () => api.post(base, { kind, title: String(f.get('title') ?? '').trim() || null, body: String(f.get('body') ?? '').trim(), anonymous: f.get('anonymous') === 'on' }),
                kind === 'gratitud' ? '¡Gracias por compartir!' : 'Gracias. Pastoral lo revisará y se publicará pronto.',
              ).then((ok) => ok && form.reset());
            }}
          >
            {tab === 'testimonios' ? <p className="small muted">Pastoral lo revisa antes de publicarlo.</p> : null}
            {tab === 'testimonios' ? <Field label="Título (opcional)" name="title" maxLength={120} /> : null}
            <div className="field">
              <label htmlFor="t-body">{tab === 'gratitud' ? 'Tu gratitud' : 'Tu testimonio'}</label>
              <textarea id="t-body" name="body" required rows={tab === 'gratitud' ? 2 : 5} maxLength={tab === 'gratitud' ? 500 : 4000} />
            </div>
            <label className="check"><input type="checkbox" name="anonymous" /> Publicar sin mi nombre</label>
            <Button type="submit" busy={busy}>Compartir</Button>
          </form>
        </Card>
      ) : null}
      {shown.length === 0 ? <Card><EmptyState title={tab === 'revisar' ? 'Nada por revisar.' : 'Todavía no hay nada aquí. ¡Sé el primero!'} /></Card> : null}
      {shown.map((i) => (
        <Card key={i.id}>
          <div className="avatar-row">
            {i.author ? <Avatar personId={i.author.personId} name={i.author.name} size={32} /> : null}
            <span className="small muted">{i.author?.name ?? 'Anónimo'}{i.anonymous && i.author ? ' (anónimo)' : ''} · {day(i.createdAt)}{i.status === 'pendiente' ? ' · en revisión' : ''}</span>
          </div>
          {i.title ? <h3>{i.title}</h3> : null}
          <p className="pre-line">{i.body}</p>
          <div className="actions actions--wrap">
            {i.status === 'publicado' ? (
              <Button variant={i.amenByMe ? 'primary' : 'secondary'} busy={busy} aria-pressed={i.amenByMe} onClick={() => void run(() => api.put(`${base}/${i.id}/amen`, { on: !i.amenByMe }), null)}>
                🙏 Amén{i.amens ? ` · ${i.amens}` : ''}
              </Button>
            ) : null}
            {d.canReview && i.status === 'pendiente' ? <Button busy={busy} onClick={() => void run(() => api.put(`${base}/${i.id}/review`, { status: 'publicado' }), 'Publicado.')}>Publicar</Button> : null}
            {d.canReview && i.status !== 'oculto' ? <Button variant="ghost" busy={busy} onClick={() => void run(() => api.put(`${base}/${i.id}/review`, { status: 'oculto' }), 'Ocultado.')}>Ocultar</Button> : null}
            {i.mine ? <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('¿Borrar?')) void run(() => api.delete(`${base}/${i.id}`), 'Borrado.'); }}>Borrar</Button> : null}
          </div>
        </Card>
      ))}
    </div>
  );
}
